import React, { useCallback, useState } from 'react'
import Nestable from 'react-nestable'

import { Icon, Input, Switch, Typography } from 'antd'

import DragIcon from '../../../../../../assets/images/dragIcon.png'
import CopItem from '../../../../../../assets/images/duplicar.png'
import EmptyData from '../../../../../../assets/images/emptyCustomFields.svg'
import TrashItem from '../../../../../../assets/images/excluir.png'
import CustomModal from '../../../../../../components/CustomModal/CustomModal'
import { getMessage } from '../../../../../../util/IntlMessages'
import { useCustomFieldsContext } from '../../context/customFields'
import { useManageFieldRelationships } from '../../hooks/useManageFieldRelationships'
import { useManageField } from '../../hooks/useManageFields'
import { useManagePositions } from '../../hooks/useManagePositions'

import './styles.less'

export function CustomFieldsList() {
  const [confirmationModal, setConfirmationModal] = useState(null)

  const { groupedFields, setGroupedFields } = useCustomFieldsContext()

  const {
    fields,
    selectField,
    selectedFieldId,
    updateField,
    duplicateField,
    getFieldById,
    isLoading,
    isUninitialized,
    removeField
  } = useManageField()

  const { removeAllRelationships, hasRelationships } = useManageFieldRelationships({ fieldId: selectedFieldId })

  const { updateFieldsAfterDrag, rearrangeFieldsOnDrag } = useManagePositions()

  function handleRemoveField(fieldId) {
    const field = getFieldById(fieldId)
    if (field?.isGroup) {
      removeField(fieldId)
    } else {
      setConfirmationModal({
        onOk: () => {
          removeField(fieldId)
          setConfirmationModal(null)
        },
        onCancel: () => setConfirmationModal(null),
        fieldName: getFieldById(fieldId).fieldName
      })
    }
  }

  const onDragField = useCallback(
    ({ dragItem, items }) => {
      const { sourceField, sourceFieldGroup, targetFieldGroup, updatedFields } = rearrangeFieldsOnDrag({
        dragItem,
        items
      })

      if (!sourceField?.isGroup && sourceFieldGroup !== targetFieldGroup && hasRelationships) {
        setConfirmationModal({
          onOk: () => {
            removeAllRelationships({ targetFieldId: sourceField.id })
            updateFieldsAfterDrag(updatedFields)
            setConfirmationModal(null)
          },
          onCancel: () => {
            setGroupedFields((prev) => prev)
            setConfirmationModal(null)
          },
          fieldName: sourceField.fieldName
        })
      } else {
        updateFieldsAfterDrag(updatedFields)
      }
    },
    [fields, hasRelationships, removeAllRelationships, selectedFieldId, updateFieldsAfterDrag, rearrangeFieldsOnDrag]
  )

  if (isLoading || isUninitialized) return <Loading />
  if (groupedFields?.length === 0) return <Empty />

  return (
    <div className="customFieldsList">
      <CustomModal
        visible={!!confirmationModal}
        title={<Icon type="warning" className="confirmationModal__icon" />}
        onCancel={confirmationModal?.onCancel}
        onSubmit={confirmationModal?.onOk}
        useBottomButtons={true}
        okText={'confirm'}
        cancelText={'cancel'}
        closable={true}
        centered={true}
        className={'confirmationModal'}
      >
        <div className="confirmationModal__content">
          <Typography.Text>
            {getMessage('customFieldRemoveFieldConfirm', confirmationModal?.fieldName || '')}
          </Typography.Text>
          <Typography.Text>{getMessage('customFieldRemoveFieldMessage')}</Typography.Text>
        </div>
      </CustomModal>

      <Nestable
        items={groupedFields}
        maxDepth={2}
        onChange={onDragField}
        className="customFieldsList__list"
        renderItem={({ item }) => {
          const field = getFieldById(item.id)

          if (!field) return null

          const showDeleteOption = field.isGroup ? !field.children?.length > 0 : true

          return (
            <div
              key={field.id}
              className={`customFieldsList__item ${selectedFieldId == field.id ? 'customFieldsList__item--active' : ''}`}
              onMouseDown={() => selectField(field?.id)}
            >
              <div className="customFieldsList__dragWrapper">
                {!field.isGroup && <span className="customFieldsList__dragContent">{String(field.fieldRow)}</span>}
                <img className="customFieldsList__dragIcon" src={DragIcon} alt="drag" />
              </div>

              <div className="customFieldsList__itemContent">
                <div className="customFieldsList__itemContentHeader">
                  <div className="customFieldsList__itemName">
                    <img
                      className="customFieldsList__fieldIcon"
                      alt="desc"
                      src={require(`../../../../../../assets/images/${field.internalTypeName}.png`)}
                    />
                    <Typography className="field-header-description-name">{field.fieldName}</Typography>
                  </div>

                  <div className="customFieldsList__itemActions">
                    {!field.isGroup && (
                      <div className="customFieldsList__itemActiveSwitch">
                        <Typography className="customFieldsList__itemActionsText">
                          {getMessage('statusLabel')}
                        </Typography>
                        <Switch
                          size="small"
                          checked={field.active}
                          onChange={(value) => updateField({ property: 'active', value, fieldId: field.id })}
                        />
                      </div>
                    )}

                    <div className="customFieldsList__itemActionDivider" />

                    <div className="customFieldsList__itemActions">
                      <a onClick={() => duplicateField({ fieldId: field.id })}>
                        <img src={CopItem} alt="cop-icon" />
                      </a>

                      {showDeleteOption && (
                        <a onClick={() => handleRemoveField(field.id)}>
                          <img src={TrashItem} alt="trash-icon" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {!field.isGroup && (
                  <>
                    <div className="customFieldsList__itemContentBody">
                      <Input type="text" disabled placeholder={field.fieldProperties?.placeholder} />
                    </div>

                    <div className="customFieldsList__itemContentFooter">
                      <p className={`customFieldsList__itemContentFooterText ${field.unsaved ? 'unsaved' : 'saved'}`}>
                        {field.unsaved ? 'NÂO SALVO' : 'SALVO'}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )
        }}
      />
    </div>
  )
}

const Loading = () => (
  <div className="customFieldsList">
    <Icon type="loading" className="customFieldsList__loadingIcon" spin />
    <Typography className="customFieldsList__loadingText">{getMessage('loading...')}</Typography>
  </div>
)

const Empty = () => (
  <div className="customFieldsList">
    <img className="empty-fields-image" src={EmptyData} alt="empty_data" />
    <Typography>{getMessage('clickToSelectCustomField')}</Typography>
    <Typography>{getMessage('clickToAddCustomFields')}</Typography>
  </div>
)
